import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
const faqs = [{
  question: 'What is GINEXT Ginger Oil?',
  answer: 'GINEXT is a circulation-supporting botanical oil designed to create deep, steady warmth in your legs and feet. Instead of numbing or freezing the area, it helps your body release pressure, relax tight tissues, and support natural fluid movement.'
}, {
  question: 'How is GINEXT different from typical pain or menthol creams?',
  answer: <>
        <p className="mb-3" data-id="element-483">
          <strong data-id="element-484">Most creams:</strong>
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1" data-id="element-485">
          <li data-id="element-486">Use cold shock or intense tingling</li>
          <li data-id="element-487">Overload your nerves and mask sensation</li>
          <li data-id="element-488">Feel good briefly, then wear off as heaviness returns</li>
        </ul>
        <p className="mb-3" data-id="element-489">
          <strong data-id="element-490">GINEXT:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-491">
          <li data-id="element-492">Creates a gradual, deep warmth (not icy, not burning)</li>
          <li data-id="element-493">Supports circulation rather than just numbing sensation</li>
          <li data-id="element-494">Helps reduce the feeling of pressure and heaviness over time</li>
          <li data-id="element-495">Absorbs cleanly without greasy residue</li>
        </ul>
      </>
}, {
  question: 'What ingredients are in GINEXT?',
  answer: <>
        <p className="mb-3" data-id="element-496">
          GINEXT is made from high-potency ginger and complementary botanical
          oils, including:
        </p>
        <ul className="list-disc pl-5 space-y-2" data-id="element-497">
          <li data-id="element-498">
            <strong data-id="element-499">Ginger & Black Pepper</strong> – Promote deep, natural
            warmth and support blood flow
          </li>
          <li data-id="element-500">
            <strong data-id="element-501">Eucalyptus</strong> – Helps calm discomfort without fully
            numbing sensation
          </li>
          <li data-id="element-502">
            <strong data-id="element-503">Rosemary</strong> – Supports circulation and tissue recovery
          </li>
          <li data-id="element-504">
            <strong data-id="element-505">Light Botanical Oils</strong> – Absorb quickly and cleanly
            with no sticky film
          </li>
        </ul>
        <p className="mt-3" data-id="element-506">
          It is 100% natural with zero synthetics, no harsh chemicals, and no
          artificial numbing agents.
        </p>
      </>
}, {
  question: 'What does GINEXT feel like when I use it?',
  answer: <>
        <p className="mb-3" data-id="element-507">
          <strong data-id="element-508">You will not feel:</strong>
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1" data-id="element-509">
          <li data-id="element-510">An icy blast</li>
          <li data-id="element-511">Aggressive tingling</li>
          <li data-id="element-512">Burning heat</li>
        </ul>
        <p className="mb-3" data-id="element-513">
          <strong data-id="element-514">You will likely feel:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-515">
          <li data-id="element-516">Warmth that arrives gradually, then deepens</li>
          <li data-id="element-517">Pressure easing instead of being masked</li>
          <li data-id="element-518">Legs that feel lighter and less "stuck" by evening</li>
          <li data-id="element-519">Skin that feels normal, not coated or greasy</li>
        </ul>
      </>
}, {
  question: 'What kinds of leg issues is GINEXT used for?',
  answer: <>
        <p className="mb-3" data-id="element-520">Customers use GINEXT daily to help with:</p>
        <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-521">
          <li data-id="element-522">Heavy, tight, or "loaded" legs</li>
          <li data-id="element-523">Ankle and foot swelling</li>
          <li data-id="element-524">End-of-day pressure and sock marks</li>
          <li data-id="element-525">Stiffness from long hours sitting or standing</li>
          <li data-id="element-526">Discomfort from work, travel, or time on your feet</li>
        </ul>
        <p data-id="element-527">
          It's designed as daily support to prevent buildup, not just an
          emergency spot-treatment.
        </p>
      </>
}, {
  question: 'How do I use GINEXT?',
  answer: <>
        <p className="mb-3" data-id="element-528">Using GINEXT is simple:</p>
        <ol className="list-decimal pl-5 space-y-2 mb-3" data-id="element-529">
          <li data-id="element-530">Apply a few drops to the area (legs, ankles, or feet).</li>
          <li data-id="element-531">Massage gently into the skin until absorbed.</li>
          <li data-id="element-532">Wait a few minutes for warmth to gradually build.</li>
          <li data-id="element-533">Use daily or as needed for ongoing support.</li>
        </ol>
        <p data-id="element-534">
          No special rituals, no long waiting period — it's meant to fit into
          real daily routines.
        </p>
      </>
}, {
  question: 'How often should I apply it?',
  answer: <>
        <p className="mb-3" data-id="element-535">Most people use GINEXT once or twice a day:</p>
        <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-536">
          <li data-id="element-537">In the morning, to prevent that end-of-day heavy feeling</li>
          <li data-id="element-538">In the evening, to unwind legs after a long day</li>
        </ul>
        <p data-id="element-539">
          Consistent, daily use tends to provide the most noticeable change over
          time.
        </p>
      </>
}, {
  question: 'How long until I feel results?',
  answer: <>
        <p className="mb-3" data-id="element-540">Many customers report feeling:</p>
        <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-541">
          <li data-id="element-542">Gentle warmth within minutes</li>
          <li data-id="element-543">
            Less pressure and heaviness by evening when used earlier in the day
          </li>
          <li data-id="element-544">
            More consistent comfort and lighter-feeling legs with regular daily
            use over 30–60 days
          </li>
        </ul>
        <p data-id="element-545">
          Individual experiences may vary, but the formula is designed for both
          immediate support and ongoing relief.
        </p>
      </>
}, {
  question: 'Will it feel greasy or stain my clothes?',
  answer: <>
        <p className="mb-3" data-id="element-546">
          No. GINEXT is formulated with fast-absorbing botanical oils that:
        </p>
        <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-547">
          <li data-id="element-548">Sink into the skin quickly</li>
          <li data-id="element-549">Leave no greasy film</li>
          <li data-id="element-550">Don't leave a heavy or sticky residue</li>
        </ul>
        <p data-id="element-551">You can apply it and get on with your day.</p>
      </>
}, {
  question: 'Is GINEXT safe for daily use?',
  answer: <>
        <p className="mb-3" data-id="element-552">GINEXT is:</p>
        <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-553">
          <li data-id="element-554">Made with 100% natural plant-based ingredients</li>
          <li data-id="element-555">Free from menthol overload</li>
          <li data-id="element-556">
            Free from harsh synthetic chemicals and artificial numbing agents
          </li>
        </ul>
        <p data-id="element-557">
          As with any topical product, if you have sensitive skin or known
          allergies to essential oils, test a small patch first. Discontinue use
          if irritation occurs and consult your healthcare provider if needed.
        </p>
      </>
}, {
  question: 'Who is GINEXT for?',
  answer: <>
        <p className="mb-3" data-id="element-558">GINEXT is ideal for adults who:</p>
        <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-559">
          <li data-id="element-560">Spend long hours standing or sitting</li>
          <li data-id="element-561">
            Notice sock imprints, ankle puffiness, or end-of-day leg pressure
          </li>
          <li data-id="element-562">Feel that cold creams help only briefly or not at all</li>
          <li data-id="element-563">Want warm, natural support instead of numbing gels</li>
        </ul>
        <p data-id="element-564">
          It's built for real legs, real routines — not just athletes or
          emergency use.
        </p>
      </>
}, {
  question: 'Can I use GINEXT alongside other products?',
  answer: <>
        <p className="mb-3" data-id="element-565">Many customers use GINEXT alongside:</p>
        <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-566">
          <li data-id="element-567">Compression socks</li>
          <li data-id="element-568">Light stretching or walking routines</li>
          <li data-id="element-569">General wellness practices</li>
        </ul>
        <p data-id="element-570">
          If you are using prescription topical medications on the same area,
          check with your healthcare provider before combining products.
        </p>
      </>
}, {
  question: 'Which bundle should I choose?',
  answer: <>
        <p className="mb-3" data-id="element-571">It depends on how you plan to use GINEXT:</p>
        <div className="space-y-3" data-id="element-572">
          <div data-id="element-573">
            <p className="font-medium text-ginger mb-1" data-id="element-574">
              Buy 3 Get 2 Free (5 Bottles) – Best value
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm" data-id="element-575">
              <li data-id="element-576">
                Ideal if you use it on multiple areas or want long-term,
                uninterrupted relief
              </li>
              <li data-id="element-577">Most cost-effective per bottle</li>
            </ul>
          </div>
          <div data-id="element-578">
            <p className="font-medium text-ginger mb-1" data-id="element-579">
              Buy 2 Get 1 Free (3 Bottles) – Most popular
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm" data-id="element-580">
              <li data-id="element-581">Great to experience real change over 30–60 days</li>
              <li data-id="element-582">Enough for regular daily use without running out too soon</li>
            </ul>
          </div>
          <div data-id="element-583">
            <p className="font-medium text-ginger mb-1" data-id="element-584">Single Bottle</p>
            <ul className="list-disc pl-5 space-y-1 text-sm" data-id="element-585">
              <li data-id="element-586">Best for first-time testing on one area</li>
              <li data-id="element-587">Short-term use or occasional application</li>
            </ul>
          </div>
        </div>
        <p className="mt-3" data-id="element-588">
          Most customers choose a bundle so they don't have to reorder right
          when relief starts kicking in.
        </p>
      </>
}, {
  question: 'Do you offer a money-back guarantee?',
  answer: "Yes. All GINEXT bundles are covered by a 30-day money-back guarantee. If you don't feel the support you were hoping for, you can request a refund within 30 days of purchase."
}, {
  question: 'Do you offer free shipping?',
  answer: 'Yes. We offer free shipping on orders over $50. Our Buy 2 Get 1 Free and Buy 3 Get 2 Free bundles both qualify for free shipping.'
}, {
  question: 'Is my payment secure?',
  answer: 'Yes. Orders are processed through a secure checkout system to protect your information during payment.'
}, {
  question: 'How do I get started?',
  answer: <>
        <ol className="list-decimal pl-5 space-y-2 mb-3" data-id="element-589">
          <li data-id="element-590">
            Choose the bundle that matches how often you want to use GINEXT.
          </li>
          <li data-id="element-591">Complete your order through our secure checkout.</li>
          <li data-id="element-592">
            Apply a few drops daily and notice how your legs feel by evening —
            lighter, less pressured, and more comfortable.
          </li>
        </ol>
        <p className="text-ginger font-medium italic" data-id="element-593">
          Feel the warmth. Release the pressure. Move through your day lighter.
        </p>
      </>
}];
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-16 sm:py-20 bg-white" data-id="element-594">
      <div className="max-w-3xl mx-auto px-4" data-id="element-595">
        {/* Section Header - Refined and Minimal */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mb-16 sm:mb-20" data-id="element-596">
          <p className="text-ginger/60 text-xs tracking-[0.2em] uppercase mb-4 font-light" data-id="element-597">
            Questions & Answers
          </p>
          <h3 className="text-3xl sm:text-4xl font-light text-charcoal mb-6 tracking-tight" data-id="element-598">
            Frequently Asked
          </h3>
          <div className="w-12 h-px bg-ginger/30 mx-auto" data-id="element-599" />
        </motion.div>

        {/* FAQ List - Elegant Divider Style */}
        <div className="space-y-0" data-id="element-600">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.03,
          ease: [0.16, 1, 0.3, 1]
        }} className="border-b border-charcoal/10 last:border-b-0" data-id="element-601">
              {/* Question Button */}
              <button onClick={() => toggleFAQ(index)} className="w-full py-6 sm:py-8 flex items-start justify-between gap-6 text-left group transition-all duration-300" data-id="element-602">
                <span className="text-base sm:text-lg font-light text-charcoal tracking-tight leading-relaxed group-hover:text-ginger transition-colors duration-300" data-id="element-603">
                  {faq.question}
                </span>
                <div className="flex-shrink-0 mt-1" data-id="element-604">
                  <AnimatePresence mode="wait" data-id="element-605">
                    {openIndex === index ? <motion.div key="minus" initial={{
                  opacity: 0,
                  rotate: -90
                }} animate={{
                  opacity: 1,
                  rotate: 0
                }} exit={{
                  opacity: 0,
                  rotate: 90
                }} transition={{
                  duration: 0.2
                }} data-id="element-606">
                        <Minus className="w-4 h-4 text-ginger" strokeWidth={1.5} data-id="element-607" />
                      </motion.div> : <motion.div key="plus" initial={{
                  opacity: 0,
                  rotate: 90
                }} animate={{
                  opacity: 1,
                  rotate: 0
                }} exit={{
                  opacity: 0,
                  rotate: -90
                }} transition={{
                  duration: 0.2
                }} data-id="element-608">
                        <Plus className="w-4 h-4 text-charcoal/40 group-hover:text-ginger transition-colors duration-300" strokeWidth={1.5} data-id="element-609" />
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </button>

              {/* Answer Content */}
              <AnimatePresence data-id="element-610">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden" data-id="element-611">
                    <div className="pb-8 pr-10" data-id="element-612">
                      <div className="text-sm sm:text-base text-charcoal/70 leading-relaxed font-light" data-id="element-613">
                        {typeof faq.answer === 'string' ? <p data-id="element-614">{faq.answer}</p> : faq.answer}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {/* Bottom CTA - Refined */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mt-16 sm:mt-20 pt-12 border-t border-charcoal/10" data-id="element-615">
          <p className="text-sm text-charcoal/60 mb-4 font-light tracking-wide" data-id="element-616">
            Still have questions?
          </p>
          <a href="#bundles" className="inline-block text-ginger text-sm tracking-wide hover:opacity-70 transition-opacity duration-300 font-light" data-id="element-617">
            Explore Bundles →
          </a>
        </motion.div>
      </div>
    </section>;
}